import * as tus from "tus-js-client";
import { FILE_SERVICE_BASE_URL } from "../../constant/apiUrl";
import { showErrorToast } from "../../utils/Toast/toast";
import { getCurrentUserId, friendlyUploadError, isOffline } from "./uploadHelpers";
import { tusResumeStorage } from "./tusResumeStorage";

/**
 * tus (https://tus.io) resumable upload client — wraps tus-js-client against
 * content-service's TusUploadController.
 *
 * No custom retry loop here — tus-js-client's own retryDelays plus onShouldRetry bailing
 * fast when offline is enough; the retry button in StepOneForm/ExternalStepOneForm covers
 * the rest, and resumes from the last confirmed offset either way.
 *
 * Parallel uploads (PARALLEL_UPLOADS) use the backend's Concatenation extension: several
 * partials PATCHed concurrently, merged with one final Upload-Concat request. Resume also
 * works across browsers/logins for the same file — see tusResumeStorage below.
 */

// No trailing slash — Spring treats "/upload/tus/files/" as a static resource lookup, not a
// match for TusUploadController's mapping.
const TUS_ENDPOINT = `${FILE_SERVICE_BASE_URL}/fritolay/upload/tus/files`;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per PATCH request
const RETRY_DELAYS_MS = [0, 1000, 3000, 5000, 10000, 20000];

// Split into this many concurrent partial uploads instead of one sequential connection —
// better bandwidth use on a fast link. Override with REACT_APP_TUS_PARALLEL_UPLOADS; 1 turns
// it off.
const PARALLEL_UPLOADS = Math.max(
  1,
  parseInt(process.env.REACT_APP_TUS_PARALLEL_UPLOADS, 10) || 4
);

const getAuthHeader = () => {
  try {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
};

const parseFileIdFromUrl = (url) => {
  if (!url) return undefined;
  const segments = url.split("/").filter(Boolean);
  return segments[segments.length - 1];
};

/** Adapts a tus-js-client error to the `{response:{status}}` shape friendlyUploadError expects. */
const toFriendlyError = (error) => {
  const status = error?.originalResponse?.getStatus?.();
  if (status === undefined) return error;
  const wrapped = new Error(error.message);
  wrapped.response = { status };
  return wrapped;
};

/**
 * Uploads the first file in `acceptedFiles` via tus. Same call signature and
 * return/throw contract as the old `uploadFileResumable`.
 */
export const uploadFileResumable = (
  acceptedFiles,
  { maxFileSizeMb = 250, onProgress, signal } = {}
) => {
  const file = acceptedFiles?.[0];
  if (!file) return Promise.resolve(undefined);

  const maxBytes = (maxFileSizeMb ?? 250) * 1024 * 1024;
  if (file.size > maxBytes) {
    showErrorToast(
      `File size exceeds the ${maxFileSizeMb} MB limit. Please upload a smaller file.`
    );
    return Promise.resolve(undefined);
  }

  const user = getCurrentUserId();

  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };

    // upload.abort() can trigger tus-js-client's own onError before our rejection below
    // runs, so a plain (non-canceled) error can win the race. This flag lets fail() catch
    // that case too instead of showing it as a real failure.
    let canceledByUser = false;
    const canceledError = () => {
      const error = new Error("Upload canceled");
      error.canceled = true;
      return error;
    };

    const fail = (error) => {
      if (canceledByUser) {
        settle(reject, canceledError());
        return;
      }
      // A canceled upload isn't a failure the user needs a toast about.
      if (!error?.canceled) {
        showErrorToast(friendlyUploadError(toFriendlyError(error)));
      }
      settle(reject, error);
    };

    const upload = new tus.Upload(file, {
      endpoint: TUS_ENDPOINT,
      chunkSize: CHUNK_SIZE,
      retryDelays: RETRY_DELAYS_MS,
      removeFingerprintOnSuccess: true,
      // Server-backed instead of localStorage, so resume works across browsers/logins too.
      urlStorage: tusResumeStorage,
      parallelUploads: PARALLEL_UPLOADS,
      metadata: { filename: file.name, user: user || "" },
      headers: getAuthHeader(),
      // Namespaced by user so a shared machine can't resume someone else's upload.
      fingerprint: (f) =>
        Promise.resolve(
          ["tus", user || "anon", f.name, f.type, f.size, f.lastModified].join("-")
        ),
      onShouldRetry: (error, retryAttempt, options) => {
        const status = error?.originalResponse?.getStatus?.();
        // Fatal client errors won't be fixed by retrying.
        if (status !== undefined && status < 500 && status !== 409 && status !== 423) {
          return false;
        }
        // Fail fast instead of burning the retry ladder while offline.
        if (isOffline()) return false;
        return retryAttempt < options.retryDelays.length;
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        if (settled) return;
        if (onProgress) onProgress(bytesUploaded, bytesTotal);
      },
      onError: fail,
      onSuccess: () => {
        const fileId = parseFileIdFromUrl(upload.url);
        settle(resolve, { status: 200, data: { fileId, status: "COMPLETED" } });
      },
    });

    if (signal) {
      if (signal.aborted) {
        settle(reject, canceledError());
        return;
      }
      signal.addEventListener(
        "abort",
        () => {
          canceledByUser = true;
          upload.abort(false).catch(() => {});
          settle(reject, canceledError());
        },
        { once: true }
      );
    }

    upload
      .findPreviousUploads()
      .then((previousUploads) => {
        if (previousUploads.length > 0) {
          upload.resumeFromPreviousUpload(previousUploads[0]);
        }
        upload.start();
      })
      .catch(fail);
  });
};

export default uploadFileResumable;
